import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { HttpService } from './http-service';

export type OnboardingPath = 'upload' | 'video' | null;

export interface OnboardingHint {
  id: string;
  message: string;
  visible: boolean;
}

export type MenuToOpen = 'file' | 'image' | null;

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private currentPath: OnboardingPath = null;
  private isMobileDevice: boolean = false;
  private currentSnackBar: MatSnackBarRef<TextOnlySnackBar> | null = null;
  private menuOpenerCallback: ((menu: MenuToOpen) => void) | null = null;

  // Observable hints for image-to-pattern tutorial
  // Desktop: Shows tooltip on button | Mobile: Shows floating snackbar with menu instructions
  uploadImageHint$ = new BehaviorSubject<OnboardingHint>({ 
    id: 'upload_image', 
    message: 'Click here → <b>Insert Image</b>', 
    visible: false 
  });
  
  transferImageHint$ = new BehaviorSubject<OnboardingHint>({ 
    id: 'transfer_image', 
    message: 'Click here → <b>Transfer Image</b>', 
    visible: false 
  });
  
  configureTransferHint$ = new BehaviorSubject<OnboardingHint>({ 
    id: 'configure_transfer', 
    message: 'Adjust settings if needed, then click <b>Apply</b>', 
    visible: false 
  });
  
  saveReminderHint$ = new BehaviorSubject<OnboardingHint>({ 
    id: 'save_reminder', 
    message: 'Click here → <b>Save</b> to keep your work', 
    visible: false 
  });

  constructor(
    private snackBar: MatSnackBar,
    private httpService: HttpService
  ) {}

  /**
   * Set whether the device is mobile (for showing floating hints)
   */
  setMobileMode(isMobile: boolean): void {
    this.isMobileDevice = isMobile;
    console.log('[Onboarding] Mobile mode:', isMobile);
  }

  /**
   * Set callback function to open menus from the toolbar
   */
  setMenuOpenerCallback(callback: (menu: MenuToOpen) => void): void {
    this.menuOpenerCallback = callback;
  }

  /**
   * Set the onboarding path (upload or draw)
   */
  setOnboardingPath(path: OnboardingPath): void {
    this.currentPath = path;

    if (path) {
      localStorage.setItem('onboarding_path', path);
    }

    console.log('[Onboarding] Path set to:', path);
  }

  /**
   * Get the current or saved onboarding path
   */
  getOnboardingPath(): OnboardingPath {
    if (this.currentPath) {
      return this.currentPath;
    }

    const savedPath = localStorage.getItem('onboarding_path') as OnboardingPath;
    if (savedPath) {
      this.currentPath = savedPath;
    }

    return this.currentPath;
  }

  /**
   * Show/hide a hint by updating its BehaviorSubject
   * Note: Mobile devices use the checklist component for onboarding instead of snackbars
   * This method only updates the hint state for desktop tooltip display
   */
  private updateHint(hint$: BehaviorSubject<OnboardingHint>, visible: boolean): void {
    const current = hint$.value;
    hint$.next({ ...current, visible });

    // Mobile onboarding is now handled by the checklist component
    // Snackbar hints are disabled as they interfered with menu interaction
    // Desktop still shows tooltip hints on the toolbar buttons
  }

  /**
   * Show a floating snackbar hint for mobile devices
   * Provides mobile-friendly instructions that reference the menu
   * Automatically opens the relevant menu for the user
   */
  private showMobileSnackbarHint(message: string): void {
    // Dismiss any existing snackbar
    if (this.currentSnackBar) {
      this.currentSnackBar.dismiss();
    }

    // Convert desktop message to mobile-friendly message
    let mobileMessage = message;
    let menuToOpen: MenuToOpen = null;
    const t = this.httpService.getTransation();

    // Map desktop messages to mobile-friendly equivalents
    // Also determine which menu to open
    if (message.includes('Insert Image')) {
      mobileMessage = t.OnboardingHintUploadImage;
      menuToOpen = 'image';
    } else if (message.includes('Transfer Image')) {
      mobileMessage = t.OnboardingHintTransferImage;
      menuToOpen = 'image';
    } else if (message.includes('Save')) {
      mobileMessage = t.OnboardingHintSave;
      menuToOpen = 'file';
    } else if (message.includes('Apply')) {
      // This hint appears in the transfer modal, no menu navigation needed
      mobileMessage = t.OnboardingHintAdjustSettings;
    } else {
      // Strip HTML tags for other messages
      mobileMessage = message.replace(/<[^>]*>/g, '');
    }

    // Show persistent snackbar at the top-right (so it doesn't block the menu)
    // User can interact with menu without dismissing snackbar
    this.currentSnackBar = this.snackBar.open(
      mobileMessage,
      t.OnboardingHintGotIt,
      {
        duration: 0, // Persistent until dismissed
        horizontalPosition: 'end', // Right side
        verticalPosition: 'top', // Top position
        panelClass: ['onboarding-mobile-hint']
      }
    );

    // Auto-open the menu AFTER snackbar is shown
    if (menuToOpen && this.menuOpenerCallback) {
      console.log('[Onboarding] Auto-opening menu:', menuToOpen);
      // Small delay to ensure snackbar is rendered first
      setTimeout(() => {
        this.menuOpenerCallback(menuToOpen);
      }, 100);
    }

    // Track when user manually dismisses the hint
    this.currentSnackBar.onAction().subscribe(() => {
      console.log('[Onboarding] Mobile hint dismissed by user:', mobileMessage);
      this.currentSnackBar = null;
    });

    // Also dismiss when user completes the action (afterDismissed for any dismissal)
    this.currentSnackBar.afterDismissed().subscribe(() => {
      this.currentSnackBar = null;
    });
  }

  /**
   * Hide all hints
   */
  hideAllHints(): void {
    // Update all hint observables to hide
    const current1 = this.uploadImageHint$.value;
    this.uploadImageHint$.next({ ...current1, visible: false });
    
    const current2 = this.transferImageHint$.value;
    this.transferImageHint$.next({ ...current2, visible: false });
    
    const current3 = this.configureTransferHint$.value;
    this.configureTransferHint$.next({ ...current3, visible: false });
    
    const current4 = this.saveReminderHint$.value;
    this.saveReminderHint$.next({ ...current4, visible: false });

    // Dismiss mobile snackbar if showing
    if (this.currentSnackBar) {
      this.currentSnackBar.dismiss();
      this.currentSnackBar = null;
    }
  }

  /**
   * Tutorial Step 1: Show hint for uploading an image
   */
  showUploadImageHint(): void {
    if (localStorage.getItem('hint_upload_completed')) {
      return;
    }
    this.hideAllHints();
    this.updateHint(this.uploadImageHint$, true);
    this.trackHintShown('upload_image');
  }

  /**
   * Tutorial Step 2: Show hint for transferring image to pattern
   */
  showMoveAndScaleImageHint(): void {
    if (localStorage.getItem('hint_move_scale_completed')) {
      return;
    }
    this.hideAllHints();
    this.updateHint(this.transferImageHint$, true);
    this.trackHintShown('transfer_image');
  }

  /**
   * Tutorial Step 3: Show hint to configure transfer settings
   */
  showConfigureTransferHint(): void {
    if (localStorage.getItem('hint_configure_transfer_completed')) {
      return;
    }
    this.hideAllHints();
    this.updateHint(this.configureTransferHint$, true);
    this.trackHintShown('configure_transfer');
  }

  /**
   * Tutorial Complete: Hide all hints when transfer is complete
   */
  showImageTransferredHint(): void {
    this.hideAllHints();
    localStorage.setItem('hint_transferred_completed', 'true');
    localStorage.setItem('hint_close_transfer_completed', 'true');
  }

  /**
   * Tutorial Step 4: Show save reminder
   * Note: Hint persists until user takes action (no timeout)
   */
  showSaveReminderHint(): void {
    if (localStorage.getItem('hint_save_reminder_shown')) {
      return;
    }
    this.updateHint(this.saveReminderHint$, true);
    this.trackHintShown('save_reminder');
    
    // Auto-open save menu on mobile
    if (this.isMobileDevice && this.menuOpenerCallback) {
      console.log('[Onboarding] Auto-opening save menu on mobile');
      this.menuOpenerCallback('file');
    }
  }

  /**
   * Track when hint is shown
   */
  private trackHintShown(hintId: string): void {
    console.log('[Onboarding] Hint shown:', hintId);
  }

  /**
   * Reset all onboarding state (for testing)
   */
  resetOnboarding(): void {
    const hintKeys = [
      'hint_upload_completed',
      'hint_move_scale_completed',
      'hint_configure_transfer_completed',
      'hint_close_transfer_completed',
      'hint_transferred_completed',
      'hint_save_reminder_shown',
      'onboarding_welcome_completed',
      'onboarding_path',
      'onboarding_checklist_state',
      'onboarding_checklist_completed',
      'onboarding_tutorial_completed'
    ];

    hintKeys.forEach(key => localStorage.removeItem(key));
    this.currentPath = null;
    this.hideAllHints();
    console.log('[Onboarding] All hints and state reset');
  }
}
